import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { X, Check, Container, MessageSquare, Clock } from 'lucide-react';

// Predefined color palette
const COLOR_PALETTE = [
  // Row 1 - Primary colors
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#3b82f6', '#8b5cf6', '#ec4899',
  // Row 2 - Lighter variants
  '#fca5a5', '#fdba74', '#fde047', '#86efac', '#5eead4', '#93c5fd', '#c4b5fd', '#f9a8d4',
  // Row 3 - Darker variants  
  '#991b1b', '#c2410c', '#a16207', '#166534', '#0f766e', '#1d4ed8', '#6d28d9', '#be185d',
  // Row 4 - Neutrals
  '#ffffff', '#d1d5db', '#9ca3af', '#6b7280', '#4b5563', '#374151', '#1f2937', '#000000',
];

export const CellColorPicker = ({ 
  currentColor, 
  onColorChange, 
  children,
  disabled = false,
  containerNumber = null,
  onCommentsClick = null,
  commentCount = 0,
  onTimelineClick = null,
}) => {
  const [open, setOpen] = useState(false);

  const handleColorSelect = (color) => {
    onColorChange(color);
    setOpen(false);
  };

  const handleClearColor = () => {
    onColorChange(null);
    setOpen(false);
  };

  const handleContextMenu = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };

  // Long press handler for mobile
  const handleTouchStart = (e) => {
    if (disabled) return;
    const timer = setTimeout(() => {
      e.preventDefault();
      setOpen(true);
    }, 500); // 500ms long press
    e.target.dataset.longPressTimer = timer;
  };

  const handleTouchEnd = (e) => {
    if (e.target.dataset.longPressTimer) {
      clearTimeout(parseInt(e.target.dataset.longPressTimer));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <div 
          onContextMenu={handleContextMenu} 
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          className="w-full h-full min-h-[28px] flex items-center"
          style={{ pointerEvents: 'auto' }}
          onClick={(e) => {
            // Allow click events to propagate to children (for inline editing)
            // Only the context menu should open the color picker
          }}
        >
          {children}
        </div>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[280px] sm:w-auto p-3 sm:p-3 bg-slate-900 border-slate-700" 
        align="center"
        sideOffset={5}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-3">
          {/* Container Number Header */}
          {containerNumber && (
            <div className="flex items-center gap-2 pb-2 border-b border-slate-700">
              <Container className="h-4 w-4 sm:h-4 sm:w-4 text-blue-400 flex-shrink-0" />
              <span className="text-sm sm:text-sm font-mono font-semibold text-blue-400 truncate">
                {containerNumber}
              </span>
            </div>
          )}
          
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-white">Text Color</span>
            {currentColor && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearColor}
                className="text-slate-400 hover:text-white h-8 sm:h-7 px-2 touch-manipulation"
              >
                <X className="h-4 w-4 sm:h-3 sm:w-3 mr-1" />
                Clear
              </Button>
            )}
          </div>
          
          {/* Color Grid - 6 columns on mobile, 8 on desktop */}
          <div className="grid grid-cols-6 sm:grid-cols-8 gap-2 sm:gap-1.5">
            {COLOR_PALETTE.map((color) => (
              <button
                key={color}
                onClick={() => handleColorSelect(color)}
                className={`w-9 h-9 sm:w-6 sm:h-6 rounded-md sm:rounded-sm border transition-transform hover:scale-110 active:scale-95 flex items-center justify-center touch-manipulation ${
                  currentColor === color 
                    ? 'border-white ring-2 ring-blue-500' 
                    : 'border-slate-600 hover:border-slate-400'
                }`}
                style={{ backgroundColor: color }}
                title={color}
              >
                {currentColor === color && (
                  <Check className={`h-4 w-4 sm:h-3 sm:w-3 ${color === '#ffffff' || color === '#fde047' || color === '#86efac' ? 'text-black' : 'text-white'}`} />
                )}
              </button>
            ))}
          </div>

          {/* Custom color input */}
          <div className="flex items-center gap-3 sm:gap-2 pt-2 border-t border-slate-700">
            <input
              type="color"
              value={currentColor || '#3b82f6'}
              onChange={(e) => handleColorSelect(e.target.value)}
              className="w-10 h-10 sm:w-8 sm:h-8 rounded cursor-pointer border-0 bg-transparent touch-manipulation"
              title="Custom color"
            />
            <span className="text-xs text-slate-400">Custom color</span>
          </div>
          
          <p className="text-[10px] text-slate-500 text-center hidden sm:block">Right-click cell to change color</p>
          <p className="text-[11px] text-slate-500 text-center sm:hidden">Long-press cell to change color</p>

          {onCommentsClick && (
            <div className="pt-2 border-t border-slate-700">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setOpen(false); onCommentsClick(); }}
                className="w-full justify-start gap-2 text-slate-200 hover:text-white hover:bg-slate-800 h-8"
                data-testid="cell-comments-menu-btn"
              >
                <MessageSquare className="h-4 w-4 text-blue-400" />
                <span>Comments</span>
                {commentCount > 0 && (
                  <span className="ml-auto text-[11px] bg-blue-600 text-white rounded-full px-1.5 py-0.5 min-w-[20px] text-center leading-none">
                    {commentCount}
                  </span>
                )}
              </Button>
            </div>
          )}

          {onTimelineClick && (
            <div className={onCommentsClick ? '' : 'pt-2 border-t border-slate-700'}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setOpen(false); onTimelineClick(); }}
                className="w-full justify-start gap-2 text-slate-200 hover:text-white hover:bg-slate-800 h-8"
                data-testid="cell-timeline-menu-btn"
              >
                <Clock className="h-4 w-4 text-cyan-400" />
                <span>Timeline</span>
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CellColorPicker;
